import React, { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Sparkles, Send, Loader2, ShieldCheck, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { RichText } from './copilot/CopilotParts';
import { CopilotTravel } from './copilot/CopilotTravel';
import { CopilotMatch } from './copilot/CopilotMatch';
import { CopilotActionPlan } from './copilot/CopilotActionPlan';
export default function CaseCopilotDialog({ open, onOpenChange, caseItem }) {
  const [answer, setAnswer] = useState('');
  const [meta, setMeta] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState('');
  const [travel, setTravel] = useState(null);
  const [travelLoading, setTravelLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [match, setMatch] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [assigning, setAssigning] = useState(null);
  const [assignResult, setAssignResult] = useState(null);
  const [sending, setSending] = useState(false);
  const [sentNote, setSentNote] = useState(null);
  const [doneIds, setDoneIds] = useState({});
  const [runningId, setRunningId] = useState(null);

  const caseId = caseItem?._id;

  const ask = useCallback(async (q) => {
    if (!caseId) return;
    setLoading(true);
    try {
      const res = await api.post('/ai/case-copilot', { case_id: caseId, question: q || undefined });
      setAnswer(res.data.answer);
      setGaps(res.data.document_gaps || []);
      setMeta({ provider: res.data.provider, model: res.data.model, disclaimer: res.data.disclaimer });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI Copilot failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  // Auto-generate a summary when the dialog opens for a case
  useEffect(() => {
    if (open && caseId) {
      setAnswer(''); setTravel(null); setQuestion(''); setPlan(null); setDoneIds({}); setMatch(null); setAssignResult(null);
      ask(null);
    }
  }, [open, caseId, ask]);

  const handleAsk = (e) => {
    e?.preventDefault();
    if (!question.trim()) return;
    ask(question.trim());
  };

  const getTravelLinks = async () => {
    if (!caseId) return;
    setTravelLoading(true);
    try {
      const res = await api.post('/ai/travel-links', { case_id: caseId });
      setTravel(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate travel links.');
    } finally {
      setTravelLoading(false);
    }
  };

  const suggestPlan = async () => {
    if (!caseId) return;
    setPlanLoading(true); setPlan(null); setDoneIds({});
    try {
      const res = await api.post('/ai/agent/plan', { case_id: caseId });
      setPlan(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate an action plan.');
    } finally {
      setPlanLoading(false);
    }
  };

  const EXECUTABLE = ['log_communication', 'add_case_note', 'create_crm_task'];

  const runAction = async (action) => {
    if (!caseId) return;
    setRunningId(action.id);
    try {
      const res = await api.post('/ai/agent/execute', { case_id: caseId, action });
      setDoneIds((d) => ({ ...d, [action.id]: res.data.message || 'Done' }));
      toast.success(res.data.message || 'Action executed.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Execution failed.');
    } finally {
      setRunningId(null);
    }
  };

  const copyText = (t) => { navigator.clipboard?.writeText(t || ''); toast.success('Copied to clipboard.'); };

  const sendIntro = async () => {
    if (!caseId || !assignResult?.draft_message) return;
    setSending(true);
    try {
      const res = await api.post('/ai/agent/send-intro', { case_id: caseId, message: assignResult.draft_message });
      setSentNote(res.data.note || 'Sent.');
      toast.success('Intro sent to patient.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send the intro.');
    } finally {
      setSending(false);
    }
  };

  const suggestMatch = async () => {
    if (!caseId) return;
    setMatchLoading(true); setMatch(null); setAssignResult(null);
    try {
      const res = await api.post('/ai/agent/match', { case_id: caseId });
      setMatch(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate matches.');
    } finally {
      setMatchLoading(false);
    }
  };

  const assign = async (kind, name, key) => {
    if (!caseId) return;
    setAssigning(key);
    try {
      const body = { case_id: caseId };
      if (kind === 'doctor') body.doctor_name = name;
      if (kind === 'hospital') body.hospital_name = name;
      const res = await api.post('/ai/agent/assign', body);
      setAssignResult(res.data);
      setSentNote(null);
      toast.success(`Assigned ${name} to the case. Intro drafted.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Assignment failed.');
    } finally {
      setAssigning(null);
    }
  };

  const typeLabels = {
    draft_message: 'Draft message', log_communication: 'Log communication',
    add_case_note: 'Case note', create_crm_task: 'CRM task',
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="text-base font-bold">AI Case Copilot</div>
              <div className="text-xs font-normal text-slate-500">
                {caseItem?.patient_name} · {caseItem?.treatment_type || 'Case assistant'}
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Document gaps */}
        {gaps.length > 0 && (
          <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3">
            <p className="text-xs font-semibold text-red-700 dark:text-red-300 mb-1">Document gaps detected</p>
            <div className="flex flex-wrap gap-1.5">
              {gaps.map((g, i) => (
                <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-red-100 dark:bg-red-800/40 text-red-700 dark:text-red-300">{g}</span>
              ))}
            </div>
          </div>
        )}

        {/* Answer */}
        <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-4 min-h-[120px]">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Analyzing case with AI…
            </div>
          ) : (
            <>
              <RichText text={answer} />
              {answer && (
                <button onClick={() => ask(null)} className="mt-3 inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700">
                  <RefreshCw className="w-3 h-3" /> Regenerate summary
                </button>
              )}
            </>
          )}
        </div>

        {/* Ask a question */}
        <form onSubmit={handleAsk} className="flex gap-2">
          <input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask about this case… e.g. Draft a document reminder email"
            className="flex-1 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
          />
          <Button type="submit" disabled={loading || !question.trim()} className="bg-amber-600 hover:bg-amber-700 text-white">
            <Send className="w-4 h-4" />
          </Button>
        </form>

        {/* Agent action plan */}
        <CopilotActionPlan EXECUTABLE={EXECUTABLE} copyText={copyText} doneIds={doneIds} plan={plan} planLoading={planLoading} runAction={runAction} runningId={runningId} suggestPlan={suggestPlan} typeLabels={typeLabels} />

        {/* Doctor & hospital match */}
        <CopilotMatch assign={assign} assignResult={assignResult} assigning={assigning} copyText={copyText} match={match} matchLoading={matchLoading} sendIntro={sendIntro} sending={sending} sentNote={sentNote} suggestMatch={suggestMatch} />

        {/* Travel links */}
        <CopilotTravel getTravelLinks={getTravelLinks} travel={travel} travelLoading={travelLoading} />

        {/* Compliance footer */}
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 p-2.5">
          <ShieldCheck className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-[11px] text-amber-800 dark:text-amber-300">
            {meta?.disclaimer || 'AI-generated guidance for staff support only. Not a medical diagnosis. A qualified human must review before acting.'}
            {meta?.model && <span className="text-amber-500"> · {meta.provider}/{meta.model}</span>}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
